import CommunicationContextPanel from "./CommunicationContextPanel";
import CommunicationMessageCard from "./CommunicationMessageCard";
import CommunicationMessageComposer from "./CommunicationMessageComposer";

const STATUS_OPTIONS = [
  { value: "new", label: "Nieuw" },
  { value: "action_needed", label: "Actie nodig" },
  { value: "waiting_customer", label: "Wacht op klant" },
  { value: "waiting_internal", label: "Wacht intern" },
  { value: "closed", label: "Gesloten" },
];

const DELIVERY_STATE_LABELS = {
  not_sent: "Niet verzonden",
  pending: "In wachtrij",
  sent: "Verzonden",
  delivered: "Afgeleverd",
  opened: "Geopend",
  clicked: "Geklikt",
  auto_replied: "Automatisch beantwoord",
  unsubscribed: "Uitgeschreven",
  bounced: "Gebounced",
  failed: "Mislukt",
  suppressed: "Onderdrukt",
  policy_rejected: "Geweigerd door beleid",
  complaint: "Spamklacht",
};

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getDeliveryStateLabel(value) {
  return DELIVERY_STATE_LABELS[String(value || "").trim()] || "Onbekend";
}

export default function CommunicationThreadDetail({
  thread,
  messages,
  loading = false,
  detailError = "",
  statusValue = "new",
  onStatusChange,
  onStatusSave,
  statusSaving = false,
  projectLinkValue = "",
  onProjectLinkChange,
  onProjectLinkSave,
  projectOptions = [],
  projectLinkSaving = false,
  canLinkProject = false,
  messageSaving = false,
  onSubmitMessage,
  markReadSaving = false,
  onMarkRead,
  portalVisibilitySaving = false,
  onPortalVisibilityToggle,
  portalReplyPolicySaving = false,
  onPortalReplyPolicyToggle,
  portalNotificationSending = false,
  onPortalNotificationSend,
}) {
  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">Gesprek laden...</div>;
  }

  if (!thread) {
    return <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">Selecteer een gesprek om details en berichten te bekijken.</div>;
  }

  return (
    <div className="space-y-4">
      <CommunicationContextPanel thread={thread} />

      {detailError ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{detailError}</div>
      ) : null}

      <section className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2">
        <label className="space-y-1 text-sm text-slate-700">
          <span>Status</span>
          <div className="flex gap-2">
            <select
              value={statusValue}
              onChange={(event) => onStatusChange?.(event.target.value)}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
              disabled={statusSaving}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={onStatusSave}
              disabled={statusSaving}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Opslaan
            </button>
          </div>
        </label>

        {canLinkProject ? (
          <label className="space-y-1 text-sm text-slate-700">
            <span>Koppel aan project</span>
            <div className="flex gap-2">
              <select
                value={projectLinkValue}
                onChange={(event) => onProjectLinkChange?.(event.target.value)}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                disabled={projectLinkSaving}
              >
                <option value="">Selecteer project</option>
                {projectOptions.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.projectNummer ? `${project.projectNummer} • ` : ""}
                    {project.naam}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={onProjectLinkSave}
                disabled={projectLinkSaving || !projectLinkValue}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Koppelen
              </button>
            </div>
          </label>
        ) : (
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            {thread?.projectId ? "Thread is gekoppeld aan een project." : "Geen projectkoppeling beschikbaar in deze context."}
          </div>
        )}
      </section>

      <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <p className="text-sm font-medium text-slate-900">Interne attentie</p>
          <p className="text-xs text-slate-500">
            {thread?.needsInternalAttention
              ? "Deze thread bevat een nog niet afgehandelde klantreactie."
              : "Geen openstaande klantreactie voor het interne team."}
          </p>
        </div>
        <button
          type="button"
          onClick={onMarkRead}
          disabled={markReadSaving || !thread?.needsInternalAttention}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {markReadSaving ? "Opslaan..." : "Markeer als gelezen"}
        </button>
      </section>

      <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div>
          <p className="text-sm font-medium text-slate-900">Klantportaal</p>
          <p className="text-xs text-slate-500">
            {thread?.visibility?.customerPortalVisible
              ? "Deze thread is zichtbaar voor de gekoppelde klant in het portaal."
              : "Deze thread is nog niet zichtbaar in het klantportaal."}
          </p>
          {thread?.visibility?.customerPortalVisible ? (
            <p className="mt-1 text-xs text-slate-500">
              {thread?.portalAllowReply !== false
                ? "De klant mag momenteel reageren in deze thread."
                : "Reageren is momenteel geblokkeerd voor de klant."}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-slate-500">
            Laatste notificatie: {getDeliveryStateLabel(thread?.deliveryState)}
            {thread?.deliveryLastEventAt ? ` • ${formatDateTime(thread.deliveryLastEventAt)}` : ""}
          </p>
          {thread?.deliveryLastError ? (
            <p className="mt-1 text-xs text-rose-600">Foutstatus: {thread.deliveryLastError}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onPortalVisibilityToggle?.(!thread?.visibility?.customerPortalVisible)}
            disabled={portalVisibilitySaving}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {portalVisibilitySaving
              ? "Opslaan..."
              : thread?.visibility?.customerPortalVisible
                ? "Verberg in klantportaal"
                : "Toon in klantportaal"}
          </button>
          <button
            type="button"
            onClick={() => onPortalReplyPolicyToggle?.(thread?.portalAllowReply === false)}
            disabled={portalReplyPolicySaving || !thread?.visibility?.customerPortalVisible}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {portalReplyPolicySaving
              ? "Opslaan..."
              : thread?.portalAllowReply !== false
                ? "Blokkeer reacties"
                : "Sta reacties toe"}
          </button>
          <button
            type="button"
            onClick={() => onPortalNotificationSend?.()}
            disabled={portalNotificationSending || !thread?.visibility?.customerPortalVisible}
            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {portalNotificationSending ? "Versturen..." : "Stuur notificatiemail"}
          </button>
        </div>
      </section>

      <section className="space-y-3">
        {Array.isArray(messages) && messages.length > 0 ? (
          messages.map((message) => <CommunicationMessageCard key={message._id} message={message} />)
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">Nog geen berichten in dit gesprek.</div>
        )}
      </section>

      <CommunicationMessageComposer
        title="Bericht toevoegen"
        submitLabel="Bericht opslaan"
        disabled={messageSaving}
        onSubmit={onSubmitMessage}
      />
    </div>
  );
}
